
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Part, SchemaType, GenerateContentResult } from "@google/generative-ai";
import { UserProfile, SavedOutfit, RetailItem, WardrobeItem, SceneConfiguration, ItemAdjustment, OutfitLayer, StyleReport } from "../types";
import { API_KEYS, GEMINI_MODELS, GEMINI_CONFIG, BACKEND_CONFIG } from "../config";
import { Logger } from "./logger";
import { ai, STYLIST_SYSTEM_INSTRUCTION, withStylistPersona, urlToBase64 } from "@shared/services/gemini-core";
export { urlToBase64 };

const FALLBACK_IMAGE_MODEL = GEMINI_MODELS.GEMINI_2_5_FLASH;

import { apiClient } from "./apiClient";

async function callBackend(endpoint: string, body: any) {
    return await apiClient.post(endpoint, body);
}

// Check if error implies model is not available or not found
const isModelAvailabilityError = (error: any): boolean => {
    const msg = error?.message || '';
    return msg.includes('404') || msg.includes('not found') || msg.includes('not available') || msg.includes('unsupported model');
};

async function withModelFallback<T>(
    primaryModel: string,
    operation: (model: string) => Promise<T>,
    fallbackModel: string = FALLBACK_IMAGE_MODEL
): Promise<T> {
    try {
        return await operation(primaryModel);
    } catch (error) {
        if (isModelAvailabilityError(error) && primaryModel !== fallbackModel) {
            Logger.warn(`Primary model ${primaryModel} failed availability check. Falling back to ${fallbackModel}.`, error);
            return await operation(fallbackModel);
        }
        throw error;
    }
}

// Helper to get preferred models from Local Storage
const getModel = (key: string, defaultValue: string): string => {
    try {
        return localStorage.getItem(key) || defaultValue;
    } catch (e) {
        return defaultValue;
    }
};

const getPreferredImageModel = () => getModel('model_image', GEMINI_CONFIG.VISION_MODEL);
const getPreferredFlashModel = () => getModel('model_flash', GEMINI_CONFIG.FLASH_TEXT);
const getPreferredProModel = () => getModel('model_pro', GEMINI_CONFIG.PRO_TEXT);
const getPreferredThinkingModel = () => getModel('model_thinking', GEMINI_CONFIG.THINKING);
const getPreferredVideoModel = () => getModel('model_video', GEMINI_CONFIG.VIDEO);
const getPreferredTryOnModel = () => getModel('model_tryon', GEMINI_CONFIG.TRY_ON);

// Helper to Convert File to base64 string
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1] || result;
            resolve(base64);
        };
        reader.onerror = (error) => {
            Logger.error('Error converting file to base64', error);
            reject(error);
        };
        reader.readAsDataURL(file);
    });
};

// Helper to create an image part for Gemini
async function getImagePart(source: string | File | { base64: string, mimeType: string }): Promise<Part> {
    let base64Data: string;
    let mimeType = 'image/jpeg'; // Default, or detect

    try {
        if (typeof source !== 'string' && 'base64' in source) {
            base64Data = source.base64;
            mimeType = source.mimeType;
        } else if (source instanceof File) {
            base64Data = await fileToBase64(source);
            mimeType = source.type;
        } else {
            base64Data = typeof source === 'string' ? await urlToBase64(source) : '';
            if (typeof source === 'string' && source.startsWith('data:')) {
                const match = source.match(/data:([^;]+);base64,/);
                if (match) mimeType = match[1];
            }
        }

        return {
            inlineData: {
                data: base64Data,
                mimeType: mimeType
            }
        };
    } catch (error) {
        Logger.error('Failed to prepare image part for Gemini API', error);
        throw error;
    }
}

// Helper for retry logic
async function withRetry<T>(fn: () => Promise<T>, retries = 3): Promise<T> {
    try {
        return await fn();
    } catch (error) {
        if (retries > 0) {
            Logger.warn(`Gemini API call failed. Retrying... attempts left: ${retries}`, error);
            await new Promise(resolve => setTimeout(resolve, 1000));
            return withRetry(fn, retries - 1); // Result aggregation
        }
        Logger.error('Gemini API call failed after retries', error);
        throw error;
    }
}


/**
 * Calls the agent orchestrator (either remote or local)
 */
export async function orchestrateAgent(message: string, context: { profile?: UserProfile, currentOutfit?: OutfitLayer[] }, sessionId?: string, userId: string = 'guest'): Promise<any> {
    if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
        const result = await callBackend('/api/v1/agent/orchestrate', { message, context, sessionId, userId });
        return result.response;
    }
    
    // Local Orchestration
    const { AgentOrchestrator } = await import('./agentOrchestrator');
    const orchestrator = new AgentOrchestrator(userId, {
        userProfile: context.profile || { membershipLevel: 'free' } as UserProfile,
        currentLayer: context.currentOutfit ? { garments: context.currentOutfit.flatMap(o => o.garments) } : { garments: [] } as any,
        savedOutfits: []
    }, sessionId);
    
    return await orchestrator.processMessage(message);
}

// --- Image Generation & Manipulation ---

export async function generateFashionImage(prompt: string): Promise<string> {
    if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
        const result = await callBackend('/api/v1/ai/generate-image', { prompt });
        return result.imageUrl;
    }
    // Existing local implementation...
    const modelName = getPreferredImageModel();
    Logger.info('Generating fashion image', { modelName, prompt });

    // imageSize is only supported by Pro Image model
    const imageConfig: any = {
        aspectRatio: '1:1',
    };
    if (modelName.includes('pro')) {
        imageConfig.imageSize = '1K';
    }

    try {
        return await withModelFallback(modelName, async (modelName) => {
            const model = ai.getGenerativeModel({ model: modelName });
            const result = await model.generateContent({
                contents: [{
                    role: 'user',
                    parts: [{ text: `Generate a high-quality fashion image of: ${prompt}. Photorealistic, 8k resolution, cinematic lighting, highly detailed fabric textures, white background.` }]
                }]
                // config/imageConfig might not be directly supported in node SDK strictly this way depending on version, 
                // but for now let's assume standard structure. 
                // However, standard SDK generateContent takes (request). 
                // If this is for Imagen (image generation), the SDK usage is accurate only if model supports it.
            });

            const response = result.response;

            for (const candidate of response.candidates || []) {
                const part = candidate.content?.parts?.[0];
                if (!part) continue;

                if (part.inlineData) {
                    Logger.info('Fashion image generated successfully.');
                    return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
                }
                if (part.text) {
                    // Sometimes models return text if they refuse to generate image
                    Logger.warn('Gemini returned text instead of image:', part.text);
                    throw new Error(part.text);
                }
            }
            throw new Error("No image generated.");
        });
    } catch (error) {

        Logger.error('Error during fashion image generation', error);
        throw error;
    }

    throw new Error("No image generated.");
}

export async function generateModelImage(userImage: File, quality: 'low' | 'medium' | 'high' = 'medium'): Promise<string> {
    Logger.info('Generating user model image', { quality });
    const imagePart = await getImagePart(userImage);
    const modelName = getPreferredImageModel();

    const imageConfig: any = {
        aspectRatio: '3:4',
    };
    if (modelName.includes('pro')) {
        imageConfig.imageSize = '1K';
    }

    try {
        return await withModelFallback(modelName, async (modelName) => {
            const model = ai.getGenerativeModel({ model: modelName });
            const result = await model.generateContent({
                contents: [{
                    role: 'user',
                    parts: [
                        imagePart,
                        { text: "Generate a full-body fashion model avatar based on this person. Keep the face consistent but improve lighting and posture for virtual try-on. Output ONLY the image." }
                    ]
                }]
            });
            const response = result.response;

            for (const candidate of response.candidates || []) {
                const part = candidate.content?.parts?.[0];
                if (!part) continue;

                if (part.inlineData) {
                    Logger.info('User model image generated successfully.');
                    return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
                }
                if (part.text) {
                    Logger.warn('Gemini returned text during model generation:', part.text);
                    throw new Error(part.text);
                }
            }
            throw new Error("Failed to generate model image.");
        });
    } catch (error) {

        Logger.error('Error generating user model image', error);
        throw error;
    }
    throw new Error("Failed to generate model image.");
}

export async function generateVirtualTryOn(userImage: File, garmentImage: File, category: string): Promise<string> {
    Logger.info('Generating virtual try-on image', { category });
    const userPart = await getImagePart(userImage);
    const garmentPart = await getImagePart(garmentImage);

    // Virtual Try-On typically requires a very strong model to understand physics/draping
    const modelName = getPreferredTryOnModel();

    try {
        const model = ai.getGenerativeModel({ model: modelName });
        const response = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [
                    userPart,
                    garmentPart,
                    {
                        text: `Acting as a professional virtual try-on technology. 
                    Image 1: The User. 
                    Image 2: The Garment (${category}). 
                    
                    Task: Generate a highly realistic image of the User wearing the Garment.
                    - Maintain the User's exact pose, body shape, face, and lighting.
                    - The Garment must replace the user's current clothing in that area naturally.
                    - Pay attention to fabric drape, shadows, and fit.
                    - Output ONLY the generated image.` }
                ]
            }]
        });

        return response.response.text();
    } catch (error) {
        Logger.error('Error generating virtual try-on', error);
        throw error;
    }
}

export async function generateOutfitImage(
    baseModelUrl: string,
    pose: string,
    mainGarment: WardrobeItem[] | WardrobeItem | null,
    accessories: { item: WardrobeItem; color: string }[],
    garmentColor?: string,
    extraInstructions?: string,
    adjustments?: { [itemId: string]: ItemAdjustment },
    sceneConfig?: SceneConfiguration,
    quality?: 'low' | 'medium' | 'high',
    measurements?: UserProfile['measurements']
): Promise<string> {
    Logger.info('Generating outfit image', { pose });
    const modelPart = await getImagePart(baseModelUrl);
    const parts: Part[] = [modelPart];

    let prompt = `Generate a photorealistic fashion image. High fashion photography, 8k, detailed textures.
    Subject: The person in the input image.
    Pose: ${pose}.
    `;

    const garments = Array.isArray(mainGarment) ? mainGarment : (mainGarment ? [mainGarment] : []);

    if (garments.length > 0) {
        const garmentsDesc = garments.map(g => `${g.nameKey} (Category: ${g.category || 'Garment'})`).join(', ');
        prompt += `Wearing: ${garmentsDesc}. `;
        if (garmentColor) prompt += `Color context: ${garmentColor}. `;
    }

    if (accessories.length > 0) {
        const accDesc = accessories.map(a => `${a.color || ''} ${a.item.nameKey}`).join(', ');
        prompt += `Accessories: ${accDesc}. `;
    }

    if (sceneConfig && sceneConfig.backgroundPrompt) {
        prompt += `Background: ${sceneConfig.backgroundPrompt}. `;
        if (sceneConfig.modelScale) {
            prompt += `Model Scale: ${sceneConfig.modelScale}. `;
        }
    } else {
        prompt += `Background: Neutral studio. `;
    }

    if (measurements) {
        prompt += `
        Start Body Refinement:
        Height: ${measurements.height || 'Standard'}
        Weight: ${measurements.weight || 'Standard'}
        Chest: ${measurements.chest || 'Standard'}
        Waist: ${measurements.waist || 'Standard'}
        Hips: ${measurements.hips || 'Standard'}
        Ensure the avatar's body shape and garment fit accurately reflect these biometric measurements.
        End Body Refinement.
        `;
    }

    if (extraInstructions) prompt += extraInstructions;

    parts.push({ text: prompt });

    const modelName = getPreferredImageModel();
    // imageConfig is not directly supported in generateContent for all models in same way, 
    // but assuming standard model params or specific field if usually needed.
    // For standard Gemini, we rely on prompt for aspect ratio mostly, or specific params.
    // We will pass empty config for now or standard generationConfig if needed.

    try {
        return await withModelFallback(modelName, async (modelName) => {
            const model = ai.getGenerativeModel({ model: modelName });
            const result = await withRetry<GenerateContentResult>(() => model.generateContent({
                contents: [{
                    role: 'user',
                    parts
                }]
            }));

            const response = result.response;

            for (const candidate of response.candidates || []) {
                const part = candidate.content?.parts?.[0];
                if (!part) continue;

                if (part.inlineData) {
                    Logger.info('Outfit image generated successfully.');
                    return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
                }
                if (part.text) {
                    Logger.warn('Gemini returned text during outfit generation:', part.text);
                    throw new Error(part.text);
                }
            }
            throw new Error("Failed to generate outfit image.");
        });
    } catch (error) {

        Logger.error('Error generating outfit image', error);
        throw error;
    }

    throw new Error("Failed to generate outfit image.");
}

/**
 * Transforms an image's style using the Gemini Vision model.
 */
export async function transformImageStyle(
    base64Image: string,
    mimeType: string,
    stylePrompt: string
): Promise<string | undefined> {
    if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
        const result = await callBackend('/api/v1/ai/transform-image', { base64Image, mimeType, stylePrompt });
        return result.image;
    }
    // This would normally use getImagePart and VISION_MODEL locally
    return undefined;
}

export async function generateLookbookImage(imageUrls: string[], aspectRatio: '16:9' | '9:16' | '1:1' = '16:9', layout: 'collage' | 'social' = 'collage'): Promise<string> {
    Logger.info('Generating lookbook', { imageCount: imageUrls.length, aspectRatio, layout });

    try {
        const parts: Part[] = [];
        for (const url of imageUrls) {
            parts.push(await getImagePart(url));
        }

        let layoutPrompt = "Create a stylish fashion collage layout featuring these outfits. Minimalist design.";
        if (layout === 'social') {
            layoutPrompt = "Create a high-end social media lookbook layout. This is for an influencer-style post. Include geometric shapes or elegant borders if appropriate. High-end fashion aesthetic.";
        }

        parts.push({ text: `${layoutPrompt} Aspect ratio: ${aspectRatio}.` });

        const modelName = getPreferredImageModel();

        const model = ai.getGenerativeModel({ model: modelName });
        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts
            }]
        });

        const response = result.response;

        for (const candidate of response.candidates || []) {
            const part = candidate.content?.parts?.[0];
            if (!part) continue;

            if (part.inlineData) {
                Logger.info('Lookbook image generated successfully.');
                return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
            }
            if (part.text) {
                // Models sometimes return text if image generation fails or is not supported
                throw new Error(part.text);
            }
        }
    } catch (error) {
        Logger.error('Error generating lookbook image', error);
        throw error;
    }
    throw new Error("Failed to generate lookbook image.");
}

export async function findSimilarItems(items: { name: string, url: string } | { name: string, url: string }[], userPrompt?: string, userProfile?: UserProfile): Promise<{ results: RetailItem[], groundingMetadata?: any }> {
    if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
        return await callBackend('/api/v1/ai/similar-items', { items, prompt: userPrompt, profile: userProfile });
    }
    // Existing local implementation...
    const itemsArray = Array.isArray(items) ? items : [items];
    const itemNames = itemsArray.map(i => i.name).join(' and ');
    Logger.info('Finding similar items', { itemNames });
    try {
        const parts: Part[] = [];
        for (const item of itemsArray) {
            parts.push(await getImagePart(item.url));
        }

        let prompt = `Find similar fashion items to these: ${itemNames}, available for purchase online. 
        Return a list of 5 items with name, price, retailer, product URL, and a 'matchReason' explaining why this item matches the user's style or requirements.`;

        if (userPrompt) {
            prompt += `\n\nSCENE CONTEXT: "${userPrompt}". 
            IMPORTANT: Since we are in this specific scene, please prioritize suggesting 2-3 matching accessories (like sunglasses, bags, or shoes) that would perfectly complement this vibe, in addition to similar garments.`;
        }

        if (userProfile) {
            prompt += `
            User Profile Context:
            - Color Season: ${userProfile.colorSeason || 'General'}
            - Style Persona: ${userProfile.stylePersona || 'Chic'}
            - Body Size/Type: ${userProfile.bodySize || 'Standard'}
            - Membership: ${userProfile.membershipLevel} (Suggest high-end items for premium/advanced members).
            
            Focus on items that complement ${userProfile.colorSeason || 'the user'} and fit their ${userProfile.stylePersona || 'general'} style.
            `;
        }

        parts.push({
            text: withStylistPersona(prompt)
        });

        const model = ai.getGenerativeModel({
            model: GEMINI_CONFIG.FLASH_TEXT,
            generationConfig: {
                responseMimeType: 'application/json',
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        items: {
                            type: SchemaType.ARRAY,
                            items: {
                                type: SchemaType.OBJECT,
                                properties: {
                                    name: { type: SchemaType.STRING },
                                    price: { type: SchemaType.STRING },
                                    retailer: { type: SchemaType.STRING },
                                    productUrl: { type: SchemaType.STRING },
                                    imageUrl: { type: SchemaType.STRING },
                                    matchReason: { type: SchemaType.STRING }
                                }
                            }
                        }
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts
            }]
            // tools configuration should be added to getGenerativeModel or separate if supported.
            // For now assuming tools are configured. But wait, tools need to be passed to getGenerativeModel or generateContent?
            // In new SDK, tools are in getGenerativeModel config.
        });

        const response = result.response;
        const groundingMetadata = response.candidates?.[0]?.groundingMetadata;

        // Add robust JSON parsing with error handling
        let json: any = { items: [] };
        try {
            const responseText = response.text();
            Logger.info('Parsing similar items response', { textLength: responseText.length });
            json = JSON.parse(responseText);
        } catch (parseError) {
            Logger.error('Failed to parse similar items JSON response', {
                error: parseError
            });
            // Try to extract whatever we can from the response
            try {
                // Attempt to clean and re-parse
                const txt = response.text() || '{}';
                const cleanedText = txt.replace(/[\r\n]/g, ' ').trim();
                json = JSON.parse(cleanedText);
            } catch (secondError) {
                Logger.warn('Could not recover from JSON parse error, returning empty results');
                json = { items: [] };
            }
        }

        Logger.info(`Found ${json.items?.length || 0} similar items.`);

        const itemsWithGrounding = (json.items || []).map((item: RetailItem) => ({
            ...item,
            groundingMetadata: groundingMetadata // Attach grounding metadata to each item if needed
        }));

        return {
            results: itemsWithGrounding,
            groundingMetadata: (response as any).groundingMetadata || {}
        };
    } catch (error) {
        Logger.error('Error finding similar items', error);
        throw error;
    }
}

export async function generateSocialCaption(
    outfit: SavedOutfit,
    platform: 'instagram' | 'linkedin' | 'twitter',
    language: string = 'en'
): Promise<string> {
    Logger.info('Generating social caption', { platform, language });
    try {
        const modelName = GEMINI_CONFIG.FLASH_TEXT;
        const prompt = withStylistPersona(`
        Generate an engaging ${platform} caption for this outfit.
        Outfit: ${outfit.name}
        Details: ${outfit.description || 'A stylish look by Constantina AI.'}
        Platform character: ${platform === 'instagram' ? 'Visual, uses emojis and hashtags.' : platform === 'linkedin' ? 'Professional, focused on presence and personal brand.' : 'Concise, punchy, trend-focused.'}
        Language: ${language}
        
        Response: ONLY the caption text.
        `);

        const model = ai.getGenerativeModel({ model: modelName });
        const response = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: prompt }]
            }]
        });

        return response.response.text() || '';
    } catch (error) {
        Logger.error('Error generating social caption', error);
        return outfit.name;
    }
}

export async function getFashionAdvice(prompt: string, context: string, lang: string, userProfile?: UserProfile): Promise<string> {
    if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
        const result = await callBackend('/api/v1/ai/advice', { prompt, context, lang, profile: userProfile });
        return result.advice;
    }
    // Existing local implementation...
    Logger.info('Requesting fashion advice', { lang, promptPreview: prompt.substring(0, 50) });

    let fullContext = `Context: ${context}\n\nUser Question: ${prompt}\n\n`;

    if (userProfile) {
        fullContext += `User Profile Data (USE THIS TO PERSONALIZE ADVICE):
        - Measurements: Height ${userProfile.measurements?.height || '?'}, Shape ${userProfile.faceShape || '?'}, Body ${userProfile.bodySize || '?'}
        - Color Season: ${userProfile.colorSeason || 'Unknown'} (Recommend colors that fit this season)
        - Hair/Eye Color: ${userProfile.hairColor || '?'}, ${userProfile.eyeColor || '?'}
        - Style Persona: ${userProfile.stylePersona || 'Undefined'}
        - Preferred Retailers: ${userProfile.preferredRetailers?.join(', ') || 'None'}
        `;
    }

    fullContext += `\nAnswer in ${lang}. Use deep reasoning to provide a nuanced styling advice. Refer to the user's specific attributes (like color season) if relevant to explain WHY you are making a suggestion.`;

    try {
        const model = ai.getGenerativeModel({
            model: getPreferredThinkingModel()
        });
        const response = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: fullContext }]
            }]
        });
        return response.response.text() || "Sorry, I couldn't generate advice at this time.";
    } catch (error) {
        Logger.error('Error getting fashion advice', error);
        throw error;
    }
}

export async function analyzeUserImageForAttributes(source: File | { base64: string, mimeType: string }): Promise<Partial<UserProfile>> {
    if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
        let base64: string;
        let mimeType: string;

        if ('base64' in source) {
            base64 = source.base64;
            mimeType = source.mimeType;
        } else {
            base64 = await fileToBase64(source);
            mimeType = source.type;
        }

        const result = await callBackend('/api/v1/ai/analyze-features', { base64Image: base64, mimeType });
        return result.analysis;
    }

    Logger.info('Analyzing user attributes from image.');
    // Local implementation using source
    try {
        const imagePart = await getImagePart(source);

        const model = ai.getGenerativeModel({
            model: getPreferredFlashModel(),
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        hairColor: { type: SchemaType.STRING },
                        eyeColor: { type: SchemaType.STRING },
                        faceShape: { type: SchemaType.STRING },
                        bodySize: { type: SchemaType.STRING },
                        estimatedMeasurements: {
                            type: SchemaType.OBJECT,
                            properties: {
                                height: { type: SchemaType.STRING },
                                weight: { type: SchemaType.STRING },
                                chest: { type: SchemaType.STRING },
                                waist: { type: SchemaType.STRING },
                                hips: { type: SchemaType.STRING },
                            }
                        }
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [imagePart, { text: withStylistPersona("Analyze this person's physical attributes for fashion styling. Estimate hair color, eye color, face shape, body type, and approximate measurements (height, weight, chest, waist, hips). Return JSON.") }]
            }]
        });

        const response = result.response;
        const data = JSON.parse(response.text() || '{}');
        return {
            hairColor: data.hairColor || 'unknown',
            eyeColor: data.eyeColor || 'unknown',
            faceShape: data.faceShape || 'unknown',
            bodySize: data.bodySize || 'unknown',
            measurements: data.estimatedMeasurements || {}
        };
    } catch (error) {
        Logger.error('Error analyzing user attributes', error);
        throw error;
    }
}

export async function analyzeColorAndHair(imageUrl: string, lang: string): Promise<any> {
    Logger.info('Analyzing color season and features.');
    try {
        const imagePart = await getImagePart(imageUrl);

        const model = ai.getGenerativeModel({
            model: getPreferredFlashModel(),
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        season: { type: SchemaType.STRING },
                        characteristics: {
                            type: SchemaType.OBJECT,
                            properties: {
                                skinTone: { type: SchemaType.STRING },
                                hairColor: { type: SchemaType.STRING },
                                eyeColor: { type: SchemaType.STRING },
                                faceShape: { type: SchemaType.STRING },
                                bodySize: { type: SchemaType.STRING },
                            }
                        },
                        palette: {
                            type: SchemaType.OBJECT,
                            properties: {
                                powerColors: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } }, // Hex codes
                                neutrals: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } },
                                avoidColors: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } },
                            }
                        },
                        advice: {
                            type: SchemaType.OBJECT,
                            properties: {
                                clothing: { type: SchemaType.STRING },
                                hair: { type: SchemaType.STRING },
                                makeup: { type: SchemaType.STRING },
                                jewelry: { type: SchemaType.STRING },
                            }
                        }
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [imagePart, { text: withStylistPersona(`Analyze the color season, body characteristics and personal features of the person in this image. Provide advice in ${lang}. Include estimated body size and face shape in characteristics.`) }]
            }]
        });

        const response = result.response;
        const data = JSON.parse(response.text() || '{}');
        return {
            season: data.season || 'unknown',
            characteristics: {
                skinTone: data.characteristics?.skinTone || 'unknown',
                hairColor: data.characteristics?.hairColor || 'unknown',
                eyeColor: data.characteristics?.eyeColor || 'unknown',
                faceShape: data.characteristics?.faceShape || 'unknown',
                bodySize: data.characteristics?.bodySize || 'unknown',
            },
            palette: {
                powerColors: data.palette?.powerColors || [],
                neutrals: data.palette?.neutrals || [],
                avoidColors: data.palette?.avoidColors || [],
            },
            advice: {
                clothing: data.advice?.clothing || '',
                hair: data.advice?.hair || '',
                makeup: data.advice?.makeup || '',
                jewelry: data.advice?.jewelry || '',
            }
        };
    } catch (error) {
        Logger.error('Error analyzing color and hair', error);
        throw error;
    }
}

export async function generateSocialMediaContent(
    outfitDescription: string,
    currentOutfitImageUrl: string | null,
    poseDescription?: string,
    backgroundDescription?: string
) {
    Logger.info('Generating social media content', { outfitDescription });
    try {
        const parts: Part[] = [];
        if (currentOutfitImageUrl) {
            parts.push(await getImagePart(currentOutfitImageUrl));
        }

        let context = `Outfit: "${outfitDescription}"`;
        if (poseDescription) context += `\nPose/Vibe: "${poseDescription}"`;
        if (backgroundDescription) context += `\nSetting/Background: "${backgroundDescription}"`;

        const textGenPrompt = `You are a savvy social media manager for a high-end fashion brand. 
        Based on the provided outfit image and the following context:

        ${context}

        Generate three distinct posts:
        1. LinkedIn: Professional and insightful.
        2. Twitter (X): Short, punchy, and engaging.
        3. Instagram: Visually focused and lifestyle-oriented with hashtags.`;

        parts.push({ text: textGenPrompt });

        const model = ai.getGenerativeModel({
            model: getPreferredProModel(),
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        linkedin: { type: SchemaType.STRING },
                        twitter: { type: SchemaType.STRING },
                        instagram: { type: SchemaType.STRING },
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{ parts, role: 'user' }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) throw new Error("No candidates returned from social media generation.");
        const textData = JSON.parse(response.text() || "{}");

        return {
            linkedin: { text: textData.linkedin, imageUrl: currentOutfitImageUrl },
            twitter: { text: textData.twitter, imageUrl: currentOutfitImageUrl },
            instagram: { text: textData.instagram, imageUrl: currentOutfitImageUrl },
        };
    } catch (error) {
        Logger.error('Error generating social media content', error);
        throw error;
    }
}

export async function generateStylePlan(userProfile: UserProfile, savedOutfits: SavedOutfit[], lang: string, duration: 'weekly' | 'monthly' | 'quarterly'): Promise<{ plan: string }> {
    Logger.info('Generating style plan', { duration, lang, outfitCount: savedOutfits.length });
    try {
        const outfitNames = savedOutfits.map(o => o.name).join(', ');
        const colorAnalysis = (userProfile as any).colorAnalysis;
        const colorContext = colorAnalysis
            ? `
        Color Analysis (IMPORTANT - use this to personalise all color recommendations):
        - Color Season: ${colorAnalysis.season || userProfile.colorSeason || 'Unknown'}
        - Skin Tone: ${colorAnalysis.characteristics?.skinTone || 'Unknown'}
        - Power Colors (recommend these): ${(colorAnalysis.palette?.powerColors || []).join(', ') || 'N/A'}
        - Neutral Colors: ${(colorAnalysis.palette?.neutrals || []).join(', ') || 'N/A'}
        - Colors to Avoid: ${(colorAnalysis.palette?.avoidColors || []).join(', ') || 'N/A'}
        - Clothing Advice: ${colorAnalysis.advice?.clothing || 'N/A'}`
            : `        Color Season: ${userProfile.colorSeason || 'Unknown'}`;
        const prompt = `Generate a ${duration} fashion style plan for a user with the following profile:
        Style Persona: ${userProfile.stylePersona || 'Undefined'}
        Preferred Retailers: ${JSON.stringify(userProfile.preferredRetailers)}
        Saved Outfits: ${outfitNames}
        ${colorContext}
        
        Use the color analysis data above to suggest specific outfit color combinations that suit the user's season.
        Output the plan in HTML format (no markdown code blocks), ready to display. Language: ${lang}.`;

        const model = ai.getGenerativeModel({ model: getPreferredThinkingModel() });
        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: prompt }]
            }]
        });

        return { plan: result.response.text() || '' };
    } catch (error) {
        Logger.error('Error generating style plan', error);
        throw error;
    }
}

export async function summarizePreferences(messages: any[]): Promise<string> {
    try {
        const chatHistory = messages.map(m => `${m.role}: ${m.text}`).join('\n');
        const prompt = `Summarize the user's fashion preferences based on this chat history:\n${chatHistory}`;

        const model = ai.getGenerativeModel({ model: getPreferredFlashModel() });
        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: withStylistPersona(prompt) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) return '';
        return response.text() || '';
    } catch (error) {
        Logger.warn('Error summarizing preferences', error);
        return '';
    }
}

export async function getTrendAnalysis(query: string): Promise<string> {
    Logger.info('Analyzing trends', { query });
    try {
        const model = ai.getGenerativeModel({
            model: getPreferredFlashModel(),
            // Tools are now configured in getGenerativeModel
            tools: [{ googleSearch: {} } as any],
        });
        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: withStylistPersona(`Analyze current fashion trends related to: ${query}`) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) return '';
        return response.text() || '';
    } catch (error) {
        Logger.error('Error analyzing trends', error);
        throw error;
    }
}

export async function generateStylePersona(savedOutfits: SavedOutfit[], userProfile: UserProfile, lang: string): Promise<string> {
    Logger.info('Generating style persona.');
    try {
        const outfitDescriptions = savedOutfits.map(o => `${o.name}: ${o.description}`).join('\n');
        const prompt = `Analyze these outfits and the user's profile to create a "Style Persona" description.
        Profile: ${JSON.stringify(userProfile.measurements)}
        Outfits: ${outfitDescriptions}
        
        Language: ${lang}. Keep it inspiring and concise.`;

        const model = ai.getGenerativeModel({ model: getPreferredFlashModel() });
        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: withStylistPersona(prompt) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) return '';
        return response.text() || '';
    } catch (error) {
        Logger.error('Error generating style persona', error);
        throw error;
    }
}

export async function compareOutfits(outfit1: SavedOutfit, outfit2: SavedOutfit): Promise<string> {
    Logger.info('Comparing two outfits.');
    try {
        const parts: Part[] = [];
        if (outfit1.previewImageUrl) parts.push(await getImagePart(outfit1.previewImageUrl));
        if (outfit2.previewImageUrl) parts.push(await getImagePart(outfit2.previewImageUrl));

        parts.push({ text: withStylistPersona(`Compare these two outfits. Highlight differences in style, occasion, and vibe. Output in HTML.`) });

        const model = ai.getGenerativeModel({ model: getPreferredThinkingModel() });
        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) return '';
        return response.text() || '';
    } catch (error) {
        Logger.error('Error comparing outfits', error);
        throw error;
    }
}

// --- Video ---

export async function generateShoppingLookVideo(imageBytes: string, mimeType: string, prompt: string, aspectRatio: '16:9' | '9:16' | '1:1' = '9:16'): Promise<{ operation: any }> {
    Logger.info('Initiating shopping look video generation', { prompt });
    // Video generation is not currently supported in the standard SDK version used.
    // Placeholder to allow build to pass.
    throw new Error("Video generation is currently unavailable. Please contact support or check SDK version.");
}

export async function getVideosOperationStatus(operation: any): Promise<{ operation: any }> {
    throw new Error("Video generation status check is currently unavailable.");
}

/**
 * Extracts a video URL or download URI from a Gemini API operation result.
 * Handles both older and newer (Veo 2.0) response structures.
 */
export function extractVideoUrlFromOperation(operation: any): { videoUrl?: string; downloadUri?: string } {
    const response = operation.response || operation.result || (operation.response?.result);

    // Check for direct videoUrl field (sometimes seen in simplified or older versions)
    if (response?.videoUrl) {
        return { videoUrl: response.videoUrl };
    }

    // Check for standard generatedVideos array (GenAI SDK)
    const generatedVideo = response?.generatedVideos?.[0];
    if (generatedVideo?.video?.uri) {
        return { downloadUri: generatedVideo.video.uri };
    }

    return {};
}

/**
 * Downloads a video from a URI (usually requires API key) and returns a blob URL.
 */
export async function downloadVideo(uri: string, apiKey?: string): Promise<string> {
    try {
        const url = new URL(uri);
        if (apiKey) {
            url.searchParams.set('key', apiKey);
        }

        const response = await fetch(url.toString());
        if (!response.ok) {
            throw new Error(`Failed to download video: ${response.statusText}`);
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    } catch (error) {
        Logger.error('Error downloading video', { uri, error });
        throw error;
    }
}

export async function generateCapsuleWardrobe(userProfile: UserProfile, season: string, occasion: string, lang: string): Promise<any> {
    Logger.info('Generating capsule wardrobe', { season, occasion });

    const prompt = `Create a cohesive Capsule Wardrobe for:
    - Season: ${season}
    - Occasion context: ${occasion}
    - User Color Season: ${userProfile.colorSeason || 'General'}
    - Body Type/Size: ${userProfile.bodySize || 'Standard'}
    - Style Persona: ${userProfile.stylePersona || 'Chic'}
    
    The wardrobe should contain 15-20 versatile items that mix and match well.
    Return JSON with the following structure:
    {
      "name": "Title of the Capsule",
      "description": "Brief description of the vibe",
      "colorPalette": ["Hex1", "Hex2", ...],
      "items": [
        { "category": "Tops" | "Bottoms" | "Outerwear" | "Shoes" | "Accessories", "name": "Item Name", "color": "Color Name", "reason": "Why this fits" }
      ],
      "outfitCombinations": [
        "Description of outfit 1", "Description of outfit 2", ...
      ]
    }
    Language: ${lang}.`;

    try {
        const model = ai.getGenerativeModel({
            model: getPreferredProModel(),
            generationConfig: {
                responseMimeType: "application/json"
            }
        });
        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: withStylistPersona(prompt) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) throw new Error("No candidates returned for capsule wardrobe.");
        return JSON.parse(response.text() || '{}');
    } catch (error) {
        Logger.error('Error generating capsule wardrobe', error);
        throw error;
    }
}

export const generateOutfitMetadata = async (
    outfitDescription: string,
    layer: OutfitLayer,
    language: string = 'en',
    poseDescription?: string
): Promise<{ title: string; description: string }> => {
    try {
        const generationModel = getPreferredFlashModel();
        const garments = [
            layer.mainGarment ? `Main Garment: ${layer.mainGarment.nameKey}` : '',
            ...layer.accessories.map(a => `Accessory: ${a.nameKey}`)
        ].filter(Boolean).join(', ');

        const prompt = withStylistPersona(`
        Create a high-end fashion title and description for this outfit.
        
        Outfit Details:
        - Garments: ${garments}
        - User's initial thoughts: ${outfitDescription || 'No description provided'}
        - Occasion/Vibe from Pose: ${poseDescription || 'Neutral'}
        - Target Language: ${language}

        Rules:
        1. Title: Sophisticated, max 5 words.
        2. Description: 1-2 elegant sentences.
        3. Response: ONLY JSON.

        JSON Format:
        {
          "title": "Elegant Evening Look",
          "description": "A sophisticated combination perfect for gala events."
        }
        `);

        const result = await withModelFallback(generationModel, async (m) => {
            const model = ai.getGenerativeModel({
                model: m,
                generationConfig: { responseMimeType: "application/json" }
            });
            return await model.generateContent({
                contents: [{
                    role: 'user',
                    parts: [{ text: prompt }]
                }]
            });
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) {
            return {
                title: layer.mainGarment ? `Look: ${layer.mainGarment.nameKey}` : 'My Outfit',
                description: outfitDescription || 'No description.'
            };
        }
        const text = response.text() || '{}';
        const json = JSON.parse(text);

        return {
            title: json.title || 'Untitled Outfit',
            description: json.description || (outfitDescription || 'No description.')
        };

    } catch (error) {
        Logger.error('Error generating outfit metadata', error);
        return {
            title: layer.mainGarment ? `Look: ${layer.mainGarment.nameKey}` : 'My Outfit',
            description: outfitDescription || 'No description.'
        };
    }
};


export interface CuratedSetAnalysis {
    title: string;
    description: string;
    items: {
        type: 'garment' | 'accessory';
        category: string;
        name: string;
        color: string;
        material: string;
        brand: string;
        priceEstimate: string;
        matchReason: string;
    }[];
    vibe: string;
}

export async function generateComprehensiveReport(imageUrl: string, userProfile: UserProfile, lang: string = 'en'): Promise<StyleReport> {
    Logger.info('Generating comprehensive style report');
    try {
        const imagePart = await getImagePart(imageUrl);
        const measurements = userProfile.measurements || {};
        const colorSeason = userProfile.colorSeason || 'Unknown';
        const stylePersona = userProfile.stylePersona || 'General';

        const prompt = `Perform a comprehensive multi-agent style review of this outfit for the user.
        
        USER PROFILE:
        - Measurements: ${JSON.stringify(measurements)}
        - Color Season: ${colorSeason}
        - Style Persona: ${stylePersona}
        
        Please provide feedback from three distinct internal AI agents:
        1. "The Color Consultant": Focuses on seasonal color harmony and skin tone matching.
        2. "The Silhouette Expert": Focuses on fit, proportions, and how the garments complement the body shape.
        3. "The Style Advisor": Focuses on the "vibe", stylistic alignment with the user's persona, and overall trendiness.

        Output Requirements (JSON):
        - summary: A cohesive 2-sentence overview.
        - score: A total style score out of 100.
        - colorAnalysis: { matchesSeason: boolean, season: string, colorHarmony: string, recommendations: string[] }
        - agentReviews: Array of { agentName: string, rating: number (1-5), feedback: string }
        - fitSummary: { overallStatus: string, criticalAreas: string[] }

        Language: ${lang}.`;

        const model = ai.getGenerativeModel({
            model: getPreferredProModel(), // Use Pro for deep multi-agent reasoning
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        summary: { type: SchemaType.STRING },
                        score: { type: SchemaType.NUMBER },
                        colorAnalysis: {
                            type: SchemaType.OBJECT,
                            properties: {
                                matchesSeason: { type: SchemaType.BOOLEAN },
                                season: { type: SchemaType.STRING },
                                colorHarmony: { type: SchemaType.STRING },
                                recommendations: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } }
                            }
                        },
                        agentReviews: {
                            type: SchemaType.ARRAY,
                            items: {
                                type: SchemaType.OBJECT,
                                properties: {
                                    agentName: { type: SchemaType.STRING },
                                    rating: { type: SchemaType.NUMBER },
                                    feedback: { type: SchemaType.STRING }
                                }
                            }
                        },
                        fitSummary: {
                            type: SchemaType.OBJECT,
                            properties: {
                                overallStatus: { type: SchemaType.STRING },
                                criticalAreas: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } }
                            }
                        }
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [imagePart, { text: withStylistPersona(prompt) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) throw new Error("No candidates returned for style report.");
        const reportData = JSON.parse(response.text() || '{}');

        // Ensure all required fields exist with safe defaults
        const report: StyleReport = {
            summary: reportData.summary || 'Style analysis complete.',
            score: typeof reportData.score === 'number' ? reportData.score : 0,
            colorAnalysis: {
                matchesSeason: !!reportData.colorAnalysis?.matchesSeason,
                season: reportData.colorAnalysis?.season || 'Unspecified',
                colorHarmony: reportData.colorAnalysis?.colorHarmony || 'No specific color harmony noted.',
                recommendations: Array.isArray(reportData.colorAnalysis?.recommendations) ? reportData.colorAnalysis.recommendations : []
            },
            agentReviews: Array.isArray(reportData.agentReviews) ? reportData.agentReviews.map((r: any) => ({
                agentName: r.agentName || 'Advisor',
                rating: typeof r.rating === 'number' ? r.rating : 3,
                feedback: r.feedback || 'Provides balanced feedback.'
            })) : [],
            fitSummary: {
                overallStatus: reportData.fitSummary?.overallStatus || 'No specific fit issues identified.',
                criticalAreas: Array.isArray(reportData.fitSummary?.criticalAreas) ? reportData.fitSummary.criticalAreas : []
            }
        };

        return report;
    } catch (error) {
        Logger.error('Error generating comprehensive report', error);
        throw error;
    }
}

export async function performImageEdit(sourceImageUrl: string, prompt: string, quality: 'low' | 'medium' | 'high' = 'medium'): Promise<string> {
    Logger.info('Performing image edit', { prompt });
    const imagePart = await getImagePart(sourceImageUrl);
    const modelName = getPreferredImageModel();

    const imageConfig: any = {
        aspectRatio: '3:4',
    };
    if (modelName.includes('pro')) {
        imageConfig.imageSize = '1K';
    }

    try {
        return await withModelFallback(modelName, async (modelName) => {
            const isGoldenHour = prompt.toLowerCase().includes('golden hour');
            const detailedPrompt = isGoldenHour
                ? "Apply a 'Golden Hour' lighting effect to this fashion image. Warm, soft, cinematic sunlight coming from the side. Enhance skin glow and fabric texture. Create a high-end editorial look while maintaining the person's identity."
                : `${prompt} Maintain the person's identity and facial structure exactly. The output should be a highly realistic, photorealistic image. Output ONLY the image.`;

            const model = ai.getGenerativeModel({ model: modelName });
            // imageConfig/generationConfig for image editing usually requires specific model params or just prompt.
            // We'll trust the prompt for now as standard SDK support for image edit config varies.
            const result = await model.generateContent({
                contents: [{
                    role: 'user',
                    parts: [
                        imagePart,
                        { text: detailedPrompt }
                    ]
                }]
            });

            return handleApiResponse(result);
        });
    } catch (error) {
        Logger.error('Error performing image edit', error);
        throw error;
    }
}

export async function analyzeFitConfidence(imageUrl: string, userProfile: UserProfile): Promise<{ score: number; reason: string }> {
    Logger.info('Analyzing fit confidence');
    try {
        const imagePart = await getImagePart(imageUrl);
        const prompt = `Analyze this outfit on the user. Instead of technical fit, assess the 'Style Confidence' and aesthetic harmony.
        
        User Context:
        - Color Season: ${userProfile.colorSeason || 'Unknown'}
        - Body Type: ${userProfile.bodySize || 'Unknown'}
        - Style Persona: ${userProfile.stylePersona || 'General'}

        Give a score out of 100 on how 'harmonious' and 'empowering' this look is.
        Provide a 1-sentence confidence booster explaination.
        
        Output JSON: { "score": number, "reason": "string" }`;

        const model = ai.getGenerativeModel({
            model: getPreferredFlashModel(),
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        score: { type: SchemaType.NUMBER },
                        reason: { type: SchemaType.STRING }
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [imagePart, { text: withStylistPersona(prompt) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) return { score: 85, reason: "You look stunning!" };
        const data = JSON.parse(response.text() || '{}');
        return { score: data.score || 85, reason: data.reason || "You look stunning!" };
    } catch (error) {
        Logger.error('Error analyzing fit confidence', error);
        return { score: 80, reason: "You look great!" };
    }
}

export async function analyzeFitMap(imageUrl: string, userProfile: UserProfile): Promise<{ areas: { name: string, status: 'tight' | 'perfect' | 'loose', x: number, y: number }[] }> {
    Logger.info('Analyzing fit map');
    try {
        const imagePart = await getImagePart(imageUrl);
        const measurements = userProfile.measurements || {};
        const measurementContext = `
            Height: ${measurements.height || 'N/A'}
            Weight: ${measurements.weight || 'N/A'}
            Chest: ${measurements.chest || 'N/A'}
            Waist: ${measurements.waist || 'N/A'}
            Hips: ${measurements.hips || 'N/A'}
        `;

        const prompt = `Analyze the fit of the garments in this image based on the user's measurements:
        ${measurementContext}

        Identify 3-5 key areas (e.g., shoulders, waist, hips, thighs) and determine if the fit is 'tight', 'perfect', or 'loose'.
        Provide approximate X/Y coordinates (0-100 percentage) for where this fit status is most visible on the image.

        Output JSON: { "areas": [{ "name": string, "status": "tight" | "perfect" | "loose", "x": number, "y": number }] }`;

        const model = ai.getGenerativeModel({
            model: getPreferredFlashModel(),
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        areas: {
                            type: SchemaType.ARRAY,
                            items: {
                                type: SchemaType.OBJECT,
                                properties: {
                                    name: { type: SchemaType.STRING },
                                    status: { type: SchemaType.STRING, enum: ['tight', 'perfect', 'loose'] } as any,
                                    x: { type: SchemaType.NUMBER },
                                    y: { type: SchemaType.NUMBER }
                                }
                            }
                        }
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [imagePart, { text: prompt }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) return { areas: [] };
        const data = JSON.parse(response.text() || '{}');
        return { areas: data.areas || [] };
    } catch (error) {
        Logger.error('Error analyzing fit map', error);
        return { areas: [] };
    }
}

export async function analyzeCuratedSetFromImage(imageUrl: string, lang: string): Promise<CuratedSetAnalysis> {
    Logger.info('Analyzing curated set from image.');
    try {
        const imagePart = await getImagePart(imageUrl);
        const prompt = `Analyze this outfit image to create a "Curated Set" for a fashion app.
        Identify all visible garments and accessories.
        
        For each item, provide:
        - Type (garment or accessory)
        - Category (e.g., Tops, Bottoms, Shoes, Bags, Jackets, Dresses)
        - A descriptive Name (e.g., "Silk Blouse", "Leather Tote")
        - Color
        - Material (estimated)
        - A likely high-end Brand match (e.g., Gucci, Zara, etc.)
        - Estimated Price string (e.g., "€120")
        
        Also provide a Title and Description for the whole set.
        
        Language: ${lang}.
        Output JSON.`;

        const model = ai.getGenerativeModel({
            model: getPreferredProModel(), // Use Pro for better vision analysis
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        title: { type: SchemaType.STRING },
                        description: { type: SchemaType.STRING },
                        vibe: { type: SchemaType.STRING },
                        items: {
                            type: SchemaType.ARRAY,
                            items: {
                                type: SchemaType.OBJECT,
                                properties: {
                                    type: { type: SchemaType.STRING, enum: ['garment', 'accessory'] } as any,
                                    category: { type: SchemaType.STRING },
                                    name: { type: SchemaType.STRING },
                                    color: { type: SchemaType.STRING },
                                    material: { type: SchemaType.STRING },
                                    brand: { type: SchemaType.STRING },
                                    priceEstimate: { type: SchemaType.STRING },
                                    matchReason: { type: SchemaType.STRING }
                                }
                            }
                        }
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [imagePart, { text: withStylistPersona(prompt) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) throw new Error("No candidates returned for curated set analysis.");
        const data = JSON.parse(response.text() || '{}');
        return data as CuratedSetAnalysis;
    } catch (error) {
        Logger.error('Error analyzing curated set', error);
        throw error;
    }
}

const handleApiResponse = (result: GenerateContentResult): string => {
    const response = result.response;
    if (response.promptFeedback?.blockReason) {
        const { blockReason, blockReasonMessage } = response.promptFeedback;
        const errorMessage = `Request was blocked. Reason: ${blockReason}. ${blockReasonMessage || ''}`;
        throw new Error(errorMessage);
    }

    // Find the first image part in any candidate
    for (const candidate of response.candidates ?? []) {
        const parts = candidate.content?.parts;
        if (!parts) continue;
        const imagePart = parts.find((part: Part) => part.inlineData);
        if (imagePart?.inlineData) {
            const { mimeType, data } = imagePart.inlineData;
            return `data:${mimeType};base64,${data}`;
        }
    }

    const finishReason = response.candidates?.[0]?.finishReason;
    if (finishReason && finishReason !== 'STOP') {
        const errorMessage = `Image generation stopped unexpectedly. Reason: ${finishReason}. This often relates to safety settings.`;
        throw new Error(errorMessage);
    }
    const textFeedback = response.text?.();
    const errorMessage = `The AI model did not return an image. ` + (textFeedback ? `The model responded with text: "${textFeedback}"` : "This can happen due to safety filters or if the request is too complex. Please try a different image.");
    throw new Error(errorMessage);
};

export async function generateMagazineNarrative(outfits: SavedOutfit[], theme: string, userProfile: UserProfile, lang: string): Promise<string> {
    Logger.info('Generating Magazine Editorial Narrative.');
    try {
        const outfitDescriptions = outfits.map(o => o.name + (o.description ? `: ${o.description}` : '')).join(', ');
        const prompt = `Write an editorial narrative for a fashion magazine lookbook featuring these outfits: ${outfitDescriptions}.
        ${theme ? `The theme is: "${theme}".` : ''}
        Archetype/Style Persona: ${userProfile.stylePersona || userProfile.archetype || 'Fashion-forward'}.
        
        Output 2-3 beautifully written paragraphs that tell a story, capturing the essence of the theme and the outfits. Language: ${lang}.`;

        const model = ai.getGenerativeModel({
            model: getPreferredFlashModel(),
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: withStylistPersona(prompt) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) return 'A beautiful collection of curated styles.';
        return response.text() || 'A beautiful collection of curated styles.';
    } catch (error) {
        Logger.error('Error generating Magazine Narrative', error);
        throw error;
    }
}

export async function generateAttitudeNote(outfitContext: string, userProfile: UserProfile, lang: string): Promise<{ attitudeNote: string, postureGuidance: string }> {
    Logger.info('Generating Attitude & Poise note.');
    try {
        const prompt = `Based on the following outfit and user profile context, generate a short, punchy "Attitude Note" (a mindset or mood mantra for wearing this outfit) and "Posture Guidance" (how to hold oneself to embody the look).
        
        Outfit Context: ${outfitContext}
        Style Persona: ${userProfile.stylePersona || 'Undefined'}
        Life Sphere: ${userProfile.activeLifeSphere || 'Unknown'}
        
        Language: ${lang}.
        Output JSON with 'attitudeNote' (1-2 sentences) and 'postureGuidance' (1 sentence).`;

        const model = ai.getGenerativeModel({
            model: getPreferredFlashModel(),
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: SchemaType.OBJECT,
                    properties: {
                        attitudeNote: { type: SchemaType.STRING },
                        postureGuidance: { type: SchemaType.STRING }
                    }
                }
            }
        });

        const result = await model.generateContent({
            contents: [{
                role: 'user',
                parts: [{ text: withStylistPersona(prompt) }]
            }]
        });

        const response = result.response;
        if (!response.candidates || response.candidates.length === 0) throw new Error("No candidates returned for attitude note.");
        const data = JSON.parse(response.text() || '{}');
        return {
            attitudeNote: data.attitudeNote || '',
            postureGuidance: data.postureGuidance || ''
        };
    } catch (error) {
        Logger.error('Error generating Attitude Note', error);
        throw error;
    }
}

